<script setup lang="ts">
import { computed, toRef, watch } from 'vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { storeToRefs } from 'pinia';
import { useDialogPluginComponent } from 'quasar';
import { useI18n } from 'vue-i18n';
import { getSupportedLocales } from '@/plugins/i18n';

import LocalizedInput from '@/components/LocalizedInput.vue';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import LocalizedSelect from '@/components/base/LocalizedSelect.vue';
import ReorderableTable from '@/components/ReorderableTable.vue';

import { useLanguageValidationErrors } from '@/composables';
import { offeredBlockType } from '@/helpers/block.helper';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { localizableString } from '@/helpers/validation.helper';
import { getTranslation, provideEditLocale } from '@/composables/data-display-utils';
import { useTheme } from '@/composables/useTheme';
import { useBlockParent } from '@/composables/useBlockParent';
import type {
  LocalizableString,
  ProjectModuleDto,
  ProjectModuleConsultationConfigDto,
  ProjectConsultationListItemDto,
  Locales,
} from '@/services/types/api.types';
import { useConfigurationStore, useBlocksStore, useProjectStore } from '@/stores';
import { Constants } from '@/constants';
import type { BlockParent, BlockRouteFor } from '@/types/modules';

// DIALOG SET-UP
const emit = defineEmits([...useDialogPluginComponent.emits]);
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

// PROPS
const props = defineProps<{
  module: ProjectModuleDto;
  languageEnabled: Locales[];
  mode: string;
  parent: BlockParent;
  blockRouteFor: BlockRouteFor;
}>();

// CONST
const { t } = useI18n();

// STORE
const configurationStore = useConfigurationStore();
const blocksStore = useBlocksStore();
const projectStore = useProjectStore();
const { consultations, isLoading } = storeToRefs(projectStore);

// FORM
const { handleSubmit, errors, values, setFieldValue, resetForm } = useForm({
  initialValues: props.module,
  validationSchema: yup.object<any>({
    title: yup.lazy((value) => {
      if (values.block_type === Constants.Block.Type.Consultation) {
        return yup.object().nullable();
      } else {
        return localizableString(props.languageEnabled);
      }
    }),
  }),
});

const onSubmit = handleSubmit(async (values) => {
  if (values.block_type === Constants.Block.Type.Text) {
    values.config = blocksStore.createTextImageModuleConfig();
  } else if (values.block_type === Constants.Block.Type.Video) {
    values.config = blocksStore.createVideoModuleConfig();
  } else if (values.block_type === Constants.Block.Type.Consultation) {
    const moduleConfig = values.config as ProjectModuleConsultationConfigDto;
    values.config = blocksStore.createConsultationModuleConfig(moduleConfig.consultationId);
  } else if (values.block_type === Constants.Block.Type.Attachments) {
    values.config = blocksStore.createAttachmentsModuleConfig();
  } else if (values.block_type === Constants.Block.Type.Faq) {
    values.config = blocksStore.createFaqModuleConfig();
  } else if (values.block_type === Constants.Block.Type.Subscription) {
    values.config = blocksStore.createSubscriptionModuleConfig();
  } else if (values.block_type === Constants.Block.Type.Timeline) {
    values.config = blocksStore.createTimelineModuleConfig();
  } else if (values.block_type === Constants.Block.Type.ContactList) {
    values.config = blocksStore.createContactListModuleConfig();
  } else if (values.block_type === Constants.Block.Type.Hero) {
    values.config = blocksStore.createHeroModuleConfig();
  } else if (values.block_type === Constants.Block.Type.Event) {
    values.config = blocksStore.createEventModuleConfig();
  } else if (values.block_type === Constants.Block.Type.Feedback) {
    values.config = blocksStore.createFeedbackModuleConfig();
  } else if (values.block_type === Constants.Block.Type.QuestionAnswer) {
    values.config = blocksStore.createQuestionAnswerModuleConfig();
  } else if (values.block_type === Constants.Block.Type.News) {
    values.config = blocksStore.createNewsModuleConfig();
  } else if (values.block_type === Constants.Block.Type.Map) {
    values.config = blocksStore.createMapModuleConfig();
  } else if (values.block_type === Constants.Block.Type.DocumentSubmission) {
    values.config = blocksStore.createDocumentSubmissionModuleConfig();
  }
  await wrapSavingOperation(
    async () => blocksStore.saveModule(values, props.blockRouteFor),
    () => onDialogOK(),
  );
});

// COMPUTED
const columns = computed(() => [
  {
    name: 'title',
    label: t('global.label.title'),
    field: (row: any) => getTranslation(row, 'title', props.languageEnabled),
    align: 'left',
  },
]);

const hasTitle = computed(() => {
  const title: LocalizableString = values.title;
  const locales = getSupportedLocales();
  let result = false;
  for (const locale of locales) {
    result ||= !!title?.[locale];
  }
  return result;
});

const consultationTitle = computed(() => {
  return getTranslation(values, 'title', props.languageEnabled);
});

const languageErrors = useLanguageValidationErrors(errors);
const editLocale = provideEditLocale(toRef(() => props.languageEnabled));
const moduleIds = configurationStore.getProjectModulesEnabled().map((x) => x.value);
// The types enabled on the platform that the parent's theme offers: never one its theme cannot draw. A computed: the
// parent may still be loading, as a template missing from the store is, and the list follows its theme once it arrives.
const { themeHost, themeKey } = useBlockParent(() => props.parent);
const { isOffered } = useTheme(themeHost, themeKey);
const allTypes = blocksStore.getTypes();
const moduleTypes = computed(() => allTypes.filter((x) => moduleIds.includes(x.value) && isOffered(x.value)));

// FUNCTIONS
const handleRowClick = (row: ProjectConsultationListItemDto) => {
  setFieldValue('config.consultationId', row.id);
  setFieldValue('title', row.title);
};

function resetTitle() {
  resetForm({
    values: {
      ...values,
      title: props.module.title,
    },
  });
}

const fetchConsultations = async (request: any) => {
  // Only a project hosts a consultation block, so its parent is the project to search.
  await projectStore.searchConsultations(props.module.parent_id, request.page, request.rowsPerPage, request.q);
};

// WATCH
watch(
  () => values.block_type,
  async (newValue) => {
    resetTitle();
    if (newValue === Constants.Block.Type.Consultation) {
      await fetchConsultations({ page: 1, rowsPerPage: 5, q: null });
    } else if (newValue === Constants.Block.Type.Faq) {
      const defaultTitle = {
        en: 'Frequently Asked Questions (FAQ)',
        fr: 'Foire aux questions (FAQ)',
      };
      setFieldValue('title', defaultTitle);
    } else if (newValue === Constants.Block.Type.Subscription) {
      const defaultTitle = {
        en: 'Follow the project',
        fr: 'Suivre le projet',
      };
      setFieldValue('title', defaultTitle);
    } else if (newValue === Constants.Block.Type.ContactList) {
      const defaultTitle = {
        en: "Who's Listening",
        fr: 'Qui écoute',
      };
      setFieldValue('title', defaultTitle);
    } else if (newValue === Constants.Block.Type.Feedback) {
      const defaultTitle = {
        en: 'Feedback',
        fr: 'Réactions',
      };
      setFieldValue('title', defaultTitle);
    } else if (newValue === Constants.Block.Type.QuestionAnswer) {
      const defaultTitle = {
        en: 'Questions & Answers',
        fr: 'Questions et reponses',
      };
      setFieldValue('title', defaultTitle);
    } else if (newValue === Constants.Block.Type.Map) {
      const defaultTitle = {
        en: 'Map',
        fr: 'Carte',
      };
      setFieldValue('title', defaultTitle);
    } else if (newValue === Constants.Block.Type.DocumentSubmission) {
      const defaultTitle = {
        en: 'Document submissions',
        fr: 'Dépôt de documents',
      };
      setFieldValue('title', defaultTitle);
    }
  },
);
// The type is one the list offers. The list follows the parent's theme, which may arrive after the dialog opens.
watch(
  moduleTypes,
  (types) => {
    const blockType = offeredBlockType(types, values.block_type);
    if (blockType !== values.block_type) setFieldValue('block_type', blockType);
  },
  { immediate: true },
);
</script>
<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" persistent class="create-module">
    <q-card class="q-dialog-plugin" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ t('form.dialogs.module.create') }}</div>
        <q-space />
        <language-switcher
          v-model="editLocale"
          v-if="values.block_type != Constants.Block.Type.Consultation"
          :has-validation-errors="languageErrors"
          :languageEnabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-section>
        <localized-select
          name="block_type"
          :label="t('global.label.type')"
          :editLocale="editLocale"
          :options="moduleTypes"
          emit-value
          outlined
          map-options
        />
        <div v-if="values.block_type != Constants.Block.Type.Consultation">
          <localized-input
            name="title"
            :label="t('global.label.title')"
            :editLocale="editLocale"
            :disabled="values.block_type === Constants.Block.Type.Consultation && hasTitle"
            outlined
            :language_enabled="languageEnabled"
          />
        </div>
        <div v-else-if="values.block_type === Constants.Block.Type.Consultation">
          <div v-if="hasTitle" class="consultation-title">
            <q-input
              :label="t('global.label.title')"
              v-model="consultationTitle"
              :disable="values.block_type === Constants.Block.Type.Consultation && hasTitle"
              outlined
              class="full-width"
            />
            <q-icon name="close" @click="resetTitle" />
          </div>
          <div v-else>
            <span>{{ t('form.dialogs.module.add-consultation') }}</span>
            <reorderable-table
              :rows="consultations.consultationListItems"
              :columns="columns"
              :loading="isLoading"
              itemKey="id"
              @row-click="handleRowClick"
              :disable-add-button="true"
              :disable-delete-button="true"
              :disableOrderable="true"
              :pagination-enabled="true"
              :pagination="consultations.pagination"
              @request="fetchConsultations"
              :search-enabled="true"
            />
          </div>
        </div>
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="t('global.cancel')" @click="onDialogCancel" />
        <q-btn color="primary" :label="t('global.save')" @click="onSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
<style lang="scss">
.create-module {
  .consultation-title {
    display: flex;
    flex-direction: row;
    gap: 10px;
    width: 100%;

    .q-field {
      width: inherit;
    }

    .q-icon {
      font-size: $medium-title;
      padding-top: 20px;
      cursor: pointer;
    }
  }

  .v-enter-active,
  .v-leave-active {
    transition: opacity 0.3s ease;
  }

  .v-enter-from,
  .v-leave-to {
    opacity: 0;
  }
}
</style>
